/* public/scripts/notifications.js */

import { appData, state, saveViewSettings } from '../../app/state/state.js';
import {
    escapeHTML,
    sanitizeHTML,
} from "../utils/contentUtils.js";
import {
    formatDate,
} from "../utils/dateUtils.js";

// --- Constants ---
const TOAST_TIMEOUT = 5000; // 5 seconds
const SHADE_CONTAINER_ID = 'notification-shade';
const TOAST_CONTAINER_ID = 'toast-container';
const SYSTEM_TOAST_CONTAINER_ID = 'system-toast-container';
const BACKDROP_ID = 'notification-backdrop';
const MOBILE_NOTIFICATION_MEDIA = '(max-width: 767px)';

// --- Local State ---
const toastTimeouts = new Map();
let hasNotificationLayoutListener = false;
const notificationBehavior = {
  getNotificationText: () => '',
  handleNotificationClick: () => {},
  handleDismissClick: () => {},
  handleDismissAll: () => {},
  isApprovalNotification: () => false,
};

/**
 * Registers app-owned notification behavior used by the shade/toast shell.
 * @param {object} behavior Notification behavior callbacks.
 * @param {Function} [behavior.getNotificationText] Notification text renderer.
 * @param {Function} [behavior.handleNotificationClick] Card click handler.
 * @param {Function} [behavior.handleDismissClick] Single dismiss handler.
 * @param {Function} [behavior.handleDismissAll] Dismiss-all handler.
 * @param {Function} [behavior.isApprovalNotification] Approval classifier.
 */
export function configureNotificationShell(behavior) {
  Object.assign(notificationBehavior, behavior);
}

function rerenderBoardForNotificationLayoutChange() {
  if (state.selectedItemId) {return;}

  import('../../app/view/viewController.js')
    .then((module) => {
      module.renderCurrentBoard();
    })
    .catch((error) => {
      console.error('Failed to rerender board after notification layout change:', error);
    });
}

function isMobileNotificationLayout() {
  return window.matchMedia?.(MOBILE_NOTIFICATION_MEDIA)?.matches === true;
}

function getEffectiveNotificationsPinned() {
  return state.viewSettings.notificationsPinned && !isMobileNotificationLayout();
}

function resetNotificationShadeFilters() {
  const sortPref = state.viewSettings.notificationSort || {};
  if (sortPref.group === 'none' && sortPref.filter === 'all') {return;}

  state.viewSettings.notificationSort = {
    date: sortPref.date || 'recent',
    group: 'none',
    filter: 'all',
  };
  saveViewSettings();
}

// --- Core Exportable Functions ---

/**
 * Installs notification shade shell listeners.
 * @returns {void}
 */
export function initNotificationListeners() {
  const backdrop = document.getElementById(BACKDROP_ID);
  if (backdrop) {
    backdrop.addEventListener('click', () => toggleShade(false));
  }

  if (hasNotificationLayoutListener) {return;}

  const mobileLayoutQuery = window.matchMedia?.(MOBILE_NOTIFICATION_MEDIA);
  const handleLayoutChange = () => {
    if (state.isNotificationShadeOpen) {
      toggleShade(true);
    }
  };

  if (mobileLayoutQuery?.addEventListener) {
    mobileLayoutQuery.addEventListener('change', handleLayoutChange);
  } else if (mobileLayoutQuery?.addListener) {
    mobileLayoutQuery.addListener(handleLayoutChange);
  }
  hasNotificationLayoutListener = true;
}

/**
 * Opens, closes, or toggles the notification shade.
 * @param {boolean | undefined} forceState Explicit open state when provided.
 * @returns {void}
 */
export function toggleShade(forceState) {
  const appContainer = document.getElementById('app');
  const shade = document.getElementById(SHADE_CONTAINER_ID);
  const backdrop = document.getElementById(BACKDROP_ID);

  if (!appContainer || !shade || !backdrop) {return;}

  const isPinned = getEffectiveNotificationsPinned();
  let shouldBeOpen;

  if (forceState === undefined) {
    shouldBeOpen = !shade.classList.contains('is-open');
  } else {
    shouldBeOpen = forceState;
  }

  const wasSidebarPinned = appContainer.classList.contains('notification-sidebar-pinned');

  shade.classList.toggle('is-open', shouldBeOpen);
  state.isNotificationShadeOpen = shouldBeOpen;
  document.querySelectorAll('.notification-bell-btn').forEach((bellBtn) => {
    bellBtn.classList.toggle('active', shouldBeOpen);
  });

  appContainer.classList.toggle('notification-sidebar-pinned', shouldBeOpen && isPinned);
  backdrop.classList.toggle('is-open', shouldBeOpen && !isPinned);

  const isSidebarPinned = appContainer.classList.contains('notification-sidebar-pinned');
  if (wasSidebarPinned !== isSidebarPinned) {
    rerenderBoardForNotificationLayoutChange();
  }

  if (!shouldBeOpen) {
    state.isNotificationSettingsOpen = false;
    resetNotificationShadeFilters();
  }

  if (shouldBeOpen) {
    renderNotificationShade();
  }
}

/**
 * Shows a transient toast for an unread notification.
 * @param {object} notification Notification record.
 * @returns {void}
 */
export function showToast(notification) {
  if (state.isNotificationShadeOpen) {return;}

  const container = document.getElementById(TOAST_CONTAINER_ID);
  if (!container) {return;}

  const toastId = `toast-${notification.id}`;
  const existingToast = document.getElementById(toastId);
  if (existingToast) {return;}

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.id = toastId;
  toast.setAttribute('data-id', notification.id);

  const text = notificationBehavior.getNotificationText(notification);
  const sanitizedText = sanitizeHTML(text);

  toast.innerHTML = `
    <div class="toast-content">${sanitizedText}</div>
    <button class="toast-dismiss-btn" data-id="${notification.id}">&times;</button>
  `;

  toast.querySelector('.toast-content').addEventListener('click', () => {
    notificationBehavior.handleNotificationClick(notification);
    if (toastTimeouts.has(toastId)) {
      clearTimeout(toastTimeouts.get(toastId));
      toastTimeouts.delete(toastId);
    }
    toast.remove();
  });

  toast.querySelector('.toast-dismiss-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    notificationBehavior.handleDismissClick(notification);
    if (toastTimeouts.has(toastId)) {
      clearTimeout(toastTimeouts.get(toastId));
      toastTimeouts.delete(toastId);
    }
    toast.remove();
  });

  container.prepend(toast);

  const timeout = setTimeout(() => {
    toast.remove();
    toastTimeouts.delete(toastId);
  }, TOAST_TIMEOUT);

  toastTimeouts.set(toastId, timeout);
}

/**
 * Shows a dismissible system toast without notification navigation behavior.
 * @param {string} message Plain-text message to display.
 * @param {object} [options]
 * @param {string} [options.id]
 */
export function showSystemToast(message, options = {}) {
  if (!message) {return;}

  const container = document.getElementById(SYSTEM_TOAST_CONTAINER_ID);
  if (!container) {return;}

  const toastId = options.id || `system-toast-${Date.now()}`;
  const existingToast = document.getElementById(toastId);
  if (existingToast) {
    existingToast.remove();
    if (toastTimeouts.has(toastId)) {
      clearTimeout(toastTimeouts.get(toastId));
      toastTimeouts.delete(toastId);
    }
  }

  const toast = document.createElement('div');
  toast.className = 'toast system-toast';
  toast.id = toastId;

  toast.innerHTML = `
    <div class="toast-content">${sanitizeHTML(message)}</div>
    <button class="toast-dismiss-btn" aria-label="Dismiss message">&times;</button>
  `;

  const dismissToast = () => {
    if (toastTimeouts.has(toastId)) {
      clearTimeout(toastTimeouts.get(toastId));
      toastTimeouts.delete(toastId);
    }
    toast.remove();
  };

  toast.querySelector('.toast-dismiss-btn').addEventListener('click', (event) => {
    event.stopPropagation();
    dismissToast();
  });

  container.prepend(toast);

  const timeout = setTimeout(() => {
    dismissToast();
  }, TOAST_TIMEOUT * 2);

  toastTimeouts.set(toastId, timeout);
}

/**
 * Renders unread notifications into the notification shade.
 * @returns {void}
 */
export function renderNotificationShade() {
  if (state.isNotificationSettingsOpen) {return;}
  const container = document.getElementById(SHADE_CONTAINER_ID);
  if (!container) {return;}

  const unreadNotifications = appData.notifications.filter(n => !n.read);

  const sortPref = state.viewSettings.notificationSort || { date: 'recent', group: 'none', filter: 'all' };

  // Apply Sorting & Grouping (No filtering)
  const sortedNotifications = sortNotifications(unreadNotifications, sortPref);

  const groupOptions = [
    { id: 'none', label: 'All', title: 'Show all notifications' },
    { id: 'approvals', label: 'Approvals', title: 'Group by approvals' },
    { id: 'assigned', label: 'Assigned', title: 'Group by assigned' },
    { id: 'tagged', label: 'Tagged', title: 'Group by tagged' },
  ];
  const activeGroup = groupOptions.find((option) =>
    option.id === sortPref.group) || groupOptions[0];

  const canPinShade = !isMobileNotificationLayout();
  const pinIcon = state.viewSettings.notificationsPinned
    ? `<span class="material-symbols-outlined text-xl">right_panel_close</span>`
    : `<span class="material-symbols-outlined text-xl">right_panel_open</span>`;
  const pinButtonHtml = canPinShade
    ? `
        <button id="notification-pin-btn" class="icon-btn shade-action-btn notification-pin-btn" title="${state.viewSettings.notificationsPinned ? 'Unpin sidebar' : 'Pin sidebar'}">
          ${pinIcon}
        </button>
      `
    : '';

  const settingsIcon = `<span class="material-symbols-outlined text-xl">settings</span>`;

  // [UI Update: Removed .shade-footer, added Dismiss All to list HTML]
  let listHtml = '';
  if (sortedNotifications.length > 0) {
    let currentGroup = null;

    const getGroupType = (n) => {
      if (sortPref.group === 'approvals' && notificationBehavior.isApprovalNotification(n.triggerEvent)) {return 'Approvals';}
      if (sortPref.group === 'tagged' && n.triggerEvent.startsWith('tag_')) {return 'Tagged';}
      if (sortPref.group === 'assigned' && (n.triggerEvent.includes('assign') || n.triggerEvent.includes('owner') || n.triggerEvent.includes('manager'))) {return 'Assigned';}
      if (sortPref.group !== 'none') {return 'Other';}
      return 'none';
    };

    sortedNotifications.forEach(n => {
      const groupType = getGroupType(n);
      let groupHeader = null;

      if (sortPref.group !== 'none' && groupType !== currentGroup) {
        currentGroup = groupType;
        groupHeader = groupType;
      }

      listHtml += renderNotificationCard(n, sortPref, groupHeader);
    });

    // Append Dismiss All button at the end of the content
    listHtml += `
        <div class="shade-list-actions">
           <button id="notification-dismiss-all-btn">Dismiss All</button>
        </div>
      `;
  } else {
    listHtml = '<p class="empty-shade">All caught up!</p>';
  }

  const content = `
    <div class="shade-header">
      <h3>Notifications</h3>
      <div class="shade-header-actions">
        ${pinButtonHtml}
        <button id="notification-settings-btn" class="icon-btn shade-action-btn" title="Settings">
          ${settingsIcon}
        </button>
        <button id="notification-close-btn" class="icon-btn shade-action-btn notification-close-btn" title="Close">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </div>
    
    <div class="shade-sort-tabs compacted">
      <div class="shade-sort-group-row">
        <details class="shade-filter-menu ${sortPref.group !== 'none' ? 'is-active' : ''}">
          <summary class="shade-filter-summary" title="Change notification group filter">
            <span class="shade-filter-label">${activeGroup.label}</span>
            <span class="material-symbols-outlined text-[18px]" aria-hidden="true">expand_more</span>
          </summary>
          <div class="shade-filter-options">
            ${groupOptions.map((option) => `
              <button
                class="shade-filter-option ${option.id === sortPref.group ? 'is-active' : ''}"
                data-group="${option.id}"
                title="${option.title}"
                type="button"
              >
                ${option.label}
              </button>
            `).join('')}
          </div>
        </details>
      </div>

      <div class="shade-sort-row">
        <div class="sort-arrows">
          <button class="icon-btn sort-tab-date ${sortPref.date === 'recent' ? 'active' : ''}" data-sort="recent" title="Newest First">
              <span class="material-symbols-outlined text-sm">arrow_downward</span>
          </button>
          <button class="icon-btn sort-tab-date ${sortPref.date === 'oldest' ? 'active' : ''}" data-sort="oldest" title="Oldest First">
              <span class="material-symbols-outlined text-sm">arrow_upward</span>
          </button>
        </div>
      </div>
    </div>

    <div class="shade-list">
      ${listHtml}
    </div>
    `;

  container.innerHTML = content;
  attachShadeListeners(container);

  if (container.querySelector('#notification-settings-btn')) {
    container.querySelector('#notification-settings-btn').classList.remove('active');
  }
  if (container.querySelector('.shade-sort-tabs')) {
    container.querySelector('.shade-sort-tabs').style.display = 'flex';
  }
}

// --- Internal Helper Functions ---

function renderNotificationCard(notification, sortPref, groupHeader) {
  const text = notificationBehavior.getNotificationText(notification);
  const sanitizedText = sanitizeHTML(text);
  const time = formatDate(notification.createdAt?.toDate(), 'relative');
  const isApproval = notificationBehavior.isApprovalNotification(
    notification.triggerEvent,
  ) && notification.parentId;
  const dismissButtonHtml = isApproval
    ? `<span class="card-dismiss-btn invisible pointer-events-none" aria-hidden="true">&times;</span>`
    : `<button class="card-dismiss-btn" data-id="${notification.id}" title="Dismiss">&times;</button>`;

  const headerHtml = groupHeader
    ? `<div class="notification-group-header">
         <h4>${escapeHTML(groupHeader)}</h4>
         <hr />
       </div>`
    : '';

  return `
    ${headerHtml}
    <div class="notification-card" data-id="${notification.id}">
      <div class="card-content">
        <span class="card-text">${sanitizedText}</span>
        <span class="card-time">${escapeHTML(time)}</span>
      </div>
      ${dismissButtonHtml}
    </div>
  `;
}

async function showSettingsView() {
  if (!appData.config || !appData.config.adminNotificationControls) {
    const container = document.getElementById(SHADE_CONTAINER_ID);
    const listContainer = container.querySelector('.shade-list');
    listContainer.innerHTML = '<p class="empty-shade">Loading settings...</p>';
    return;
  }
  const container = document.getElementById(SHADE_CONTAINER_ID);
  const listContainer = container.querySelector('.shade-list');
  const sortTabs = container.querySelector('.shade-sort-tabs');
  const settingsBtn = container.querySelector('#notification-settings-btn');
  const title = container.querySelector('.shade-header h3');
  const footer = container.querySelector('.shade-footer');

  if (!listContainer || !sortTabs || !settingsBtn) {return;}

  if (title) {title.textContent = 'Notification settings';}
  sortTabs.style.display = 'none';
  if (footer) {footer.style.display = 'none';}
  settingsBtn.classList.remove('active');
  settingsBtn.title = 'Back to notifications';
  settingsBtn.setAttribute('aria-label', 'Back to notifications');
  settingsBtn.innerHTML = '<span class="material-symbols-outlined text-xl">arrow_back</span>';

  // Dynamic import to break circular dependency
    const { renderNotificationSettingsPanel } = await import('../../domains/settings/settingsController.js');
  renderNotificationSettingsPanel(listContainer);
}

function attachShadeListeners(container) {
  container.querySelectorAll('.notification-card .card-content').forEach(card => {
    card.addEventListener('click', (e) => {
      const notifId = e.currentTarget.closest('.notification-card').dataset.id;
      const notification = appData.notifications.find(n => n.id === notifId);
      if (notification) {
        notificationBehavior.handleNotificationClick(notification);
      }
    });
  });

  container.querySelectorAll('.card-dismiss-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const notifId = e.currentTarget.dataset.id;
      const notification = appData.notifications.find(n => n.id === notifId);
      if (notification) {
        notificationBehavior.handleDismissClick(notification);
      }
    });
  });

  const dismissAllBtn = container.querySelector('#notification-dismiss-all-btn');
  if (dismissAllBtn) {
    dismissAllBtn.addEventListener('click', notificationBehavior.handleDismissAll);
  }

  const closeBtn = container.querySelector('#notification-close-btn');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => toggleShade(false));
  }

  const pinBtn = container.querySelector('#notification-pin-btn');
  if (pinBtn) {
    pinBtn.addEventListener('click', () => {
      const appContainer = document.getElementById('app');
      const wasSidebarPinned = appContainer?.classList.contains('notification-sidebar-pinned');
      state.viewSettings.notificationsPinned = !state.viewSettings.notificationsPinned;
      saveViewSettings();

      const shade = document.getElementById(SHADE_CONTAINER_ID);
      const backdrop = document.getElementById(BACKDROP_ID);

      const isOpen = shade.classList.contains('is-open');
      const isPinned = getEffectiveNotificationsPinned();
      appContainer.classList.toggle('notification-sidebar-pinned', isOpen && isPinned);
      backdrop.classList.toggle('is-open', isOpen && !isPinned);

      const isSidebarPinned = appContainer.classList.contains('notification-sidebar-pinned');
      if (wasSidebarPinned !== isSidebarPinned) {
        rerenderBoardForNotificationLayoutChange();
      }

      renderNotificationShade();
    });
  }

  const settingsBtn = container.querySelector('#notification-settings-btn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', () => {
      if (state.isNotificationSettingsOpen) {
        state.isNotificationSettingsOpen = false;
        renderNotificationShade();
        return;
      }

      state.isNotificationSettingsOpen = true;
      showSettingsView();
    });
  }

  container.querySelectorAll('.shade-sort-tabs .sort-tab-date').forEach(tab => {
    tab.addEventListener('click', (e) => {
      // Find the closest button element to handle click on the icon inside
      const button = e.target.closest('.sort-tab-date');
      if (!button) {return;}

      const newSort = button.dataset.sort;
      if (newSort !== state.viewSettings.notificationSort.date) {
        state.viewSettings.notificationSort.date = newSort;
        saveViewSettings();
        renderNotificationShade();
      }
    });
  });

  container.querySelectorAll('.shade-filter-option').forEach(tab => {
    tab.addEventListener('click', (e) => {
      const newGroup = e.currentTarget.dataset.group;
      state.viewSettings.notificationSort.group = newGroup || 'none';
      saveViewSettings();
      renderNotificationShade();
    });
  });
}

function sortNotifications(notifications, preference) {
  const sorted = [...notifications];
  const { date, group } = preference;

  const getGroupValue = (n) => {
    if (group === 'approvals') {
      return notificationBehavior.isApprovalNotification(n.triggerEvent) ? 0 : 1;
    }
    if (group === 'tagged') {
      return n.triggerEvent.startsWith('tag_') ? 0 : 1;
    }
    if (group === 'assigned') {
      return n.triggerEvent.includes('assign') || n.triggerEvent.includes('owner') || n.triggerEvent.includes('manager') ? 0 : 1;
    }
    return 0;
  };

  sorted.sort((a, b) => {
    const groupA = getGroupValue(a);
    const groupB = getGroupValue(b);

    if (groupA !== groupB) {
      return groupA - groupB;
    }

    // --- FIX: Sort Crash Prevention ---
    // Safe access to toMillis() in case of latency compensation
    const timeA = (a.createdAt && typeof a.createdAt.toMillis === 'function')
      ? a.createdAt.toMillis() : 0;
    const timeB = (b.createdAt && typeof b.createdAt.toMillis === 'function')
      ? b.createdAt.toMillis() : 0;

    return (date === 'recent') ? (timeB - timeA) : (timeA - timeB);
  });

  return sorted;
}
